import { formatDate } from '@seen/core/dates'
import { invariant } from '@seen/core/invariant'
import { TaskLabel } from '@seen/core/models/taskLabel/taskLabel'
import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { getReferenceString } from '@seen/core/references'
import { isEqual, sortBy } from 'lodash-es'
import type { ReactNode } from 'react'
import {
  LabelChangeAction,
  type LabelClass,
} from '#src/components/activity/LabelChangeAction.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { TaskStateText } from '#src/components/task/TaskStateText.tsx'
import type { LabelChangeData } from '#src/utils/GroupLabelActivityEntries.tsx'

export interface TaskActivityEntry {
  action: ReactNode
  task: StandardTask
  key?: string
  labelChange?: LabelChangeData
}

export function useTaskActivity(
  tasks: StandardTask[] | StandardTask[][]
): TaskActivityEntry[] {
  function processTaskHistory(
    sortedTasks: StandardTask[]
  ): TaskActivityEntry[] {
    return sortedTasks.reduce<TaskActivityEntry[]>((acc, curr) => {
      if (acc.length === 0) {
        return [
          ...(curr.ownerPractitioner?.reference !== curr.lastUpdatedBy.reference
            ? [
                {
                  action: (
                    <>
                      assigned to{' '}
                      <HoverPractitioner
                        practitioner={curr.ownerPractitioner}
                        className="font-medium text-foreground"
                      />
                    </>
                  ),
                  task: curr,
                  key: `${curr.versionId}-assigned`,
                },
              ]
            : []),
          {
            action: 'created the task',
            task: curr,
            key: `${curr.versionId}-created`,
          },
        ]
      }
      const prev = acc[acc.length - 1]
      if (
        curr.ownerPractitioner?.reference !==
        prev.task.ownerPractitioner?.reference
      ) {
        if (!curr.ownerPractitioner?.reference) {
          acc.push({
            action: 'removed assignee',
            task: curr,
          })
          return acc
        }
        if (
          curr.ownerPractitioner?.reference === curr.lastUpdatedBy.reference
        ) {
          acc.push({ action: 'self-assigned the task', task: curr })
          return acc
        }
        acc.push({
          action: (
            <>
              assigned to{' '}
              <HoverPractitioner
                practitioner={curr.ownerPractitioner}
                className="font-medium text-foreground"
              />
            </>
          ),
          task: curr,
        })
        return acc
      }
      if (curr.state !== prev.task.state) {
        acc.push({
          action: (
            <>
              changed status from{' '}
              <span className="font-medium text-foreground">
                <TaskStateText state={prev.task.state} />
              </span>{' '}
              to{' '}
              <span className="font-medium text-foreground">
                <TaskStateText state={curr.state} />
              </span>
            </>
          ),
          task: curr,
        })
        return acc
      }
      if (curr.priority !== prev.task.priority) {
        if (!prev.task.priority) {
          acc.push({
            action: (
              <>
                set priority to{' '}
                <span className="font-medium text-foreground">
                  {curr.priorityText}
                </span>
              </>
            ),
            task: curr,
          })
          return acc
        }
        if (!curr.priority) {
          acc.push({
            action: 'removed priority',
            task: curr,
          })
          return acc
        }
        acc.push({
          action: (
            <>
              changed priority from{' '}
              <span className="font-medium text-foreground">
                {prev.task.priorityText}
              </span>{' '}
              to{' '}
              <span className="font-medium text-foreground">
                {curr.priorityText}
              </span>
            </>
          ),
          task: curr,
        })
        return acc
      }
      if (curr.dueOn !== prev.task.dueOn) {
        if (!prev.task.dueOn) {
          invariant(curr.dueOn, 'Due date is required')
          acc.push({
            action: `set due date to ${formatDate(curr.dueOn)}`,
            task: curr,
          })
          return acc
        }
        if (!curr.dueOn) {
          acc.push({
            action: 'removed due date',
            task: curr,
          })
          return acc
        }
        acc.push({
          action: `changed due date from ${formatDate(prev.task.dueOn)} to ${formatDate(curr.dueOn)}`,
          task: curr,
        })
        return acc
      }

      const prevLabels = prev.task.labels.map((l) => getReferenceString(l))
      const currLabels = curr.labels.map((l) => getReferenceString(l))

      if (!isEqual(sortBy(prevLabels), sortBy(currLabels))) {
        const added = currLabels.filter((l) => !prevLabels.includes(l))
        const removed = prevLabels.filter((l) => !currLabels.includes(l))

        if (added.length > 0 || removed.length > 0) {
          acc.push({
            action: (
              <LabelChangeAction
                added={added}
                removed={removed}
                LabelClass={TaskLabel}
              />
            ),
            task: curr,
            key: `${curr.versionId}-labels`,
            labelChange: {
              added,
              removed,
              LabelClass: TaskLabel as LabelClass,
            },
          })
          return acc
        }
      }

      return acc
    }, [])
  }

  if (Array.isArray(tasks[0])) {
    // Array of arrays: process each sub-array and flatten
    return (tasks as StandardTask[][]).flatMap((history) =>
      processTaskHistory(sortBy(history, (t) => t.lastUpdated))
    )
  } else {
    // Flat array: process as a single task's history
    return processTaskHistory(
      sortBy(tasks as StandardTask[], (t) => t.lastUpdated)
    )
  }
}
